import React, { createContext, useContext, useState, type ReactNode, type JSX } from 'react';

import { useAppSelector } from 'hooks/redux';
import { aiChatSelectors } from 'app-components/flux/ai-chat';

export type ChatViewMode = 'floating' | 'docked' | 'expanded';

export const CHAT_VIEW_MODES: ChatViewMode[] = ['floating', 'docked', 'expanded'];

export const getNextChatViewMode = (current: ChatViewMode): ChatViewMode => {
  const currentIndex = CHAT_VIEW_MODES.indexOf(current);
  const nextIndex = (currentIndex + 1) % CHAT_VIEW_MODES.length;
  return CHAT_VIEW_MODES[nextIndex];
};

export const isDockedView = (viewMode: ChatViewMode): boolean => viewMode === 'docked';

interface ChatDockContextType {
  viewMode: ChatViewMode;
  setViewMode: (viewMode: ChatViewMode) => void;
  drawerWidth: number;
  setDrawerWidth: (width: number) => void;
}

const ChatDockContext = createContext<ChatDockContextType | undefined>(undefined);

const MIN_DRAWER_WIDTH = 375;
const DEFAULT_DRAWER_WIDTH = 500;

type ChatDockProviderProps = {
  children: ReactNode;
};

export const ChatDockProvider = ({ children }: ChatDockProviderProps): JSX.Element => {
  const [viewMode, setViewMode] = useState<ChatViewMode>('floating');
  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_DRAWER_WIDTH);

  // Invariant: whenever the docked drawer becomes visible, its width is the default — a resize
  // from an earlier docked session never sticks. Covers both ways it can become visible: opening
  // the chat while already in docked view, and switching to docked view while already open.
  // Adjusted here during render (React's documented pattern for deriving state from a prop/store
  // change, https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes)
  // rather than in a useEffect, so drawerWidth is already correct in the same render pass that
  // the docked drawer reads it — a useEffect would only fix it up after that first render committed
  // with the stale width, producing a visible flash from the old size to the default one.
  const isChatOpen = Boolean(useAppSelector(aiChatSelectors.isChatOpen));
  const isDockedOpen = isChatOpen && viewMode === 'docked';
  const [prevIsDockedOpen, setPrevIsDockedOpen] = useState(isDockedOpen);
  if (isDockedOpen !== prevIsDockedOpen) {
    setPrevIsDockedOpen(isDockedOpen);
    if (isDockedOpen) {
      setDrawerWidth(DEFAULT_DRAWER_WIDTH);
    }
  }

  return (
    <ChatDockContext.Provider value={{ viewMode, setViewMode, drawerWidth, setDrawerWidth }}>
      {children}
    </ChatDockContext.Provider>
  );
};

export { MIN_DRAWER_WIDTH, DEFAULT_DRAWER_WIDTH };

export const useChatDock = (): ChatDockContextType => {
  const context = useContext(ChatDockContext);
  if (!context) {
    throw new Error('useChatDock must be used within a ChatDockProvider');
  }
  return context;
};
